import LazyVideo from "@/components/LazyVideo";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronRight, ChevronLeft, Check, ArrowLeft, User, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LiquidBackground } from './LiquidBackground';
import { useT, useContent } from '@/context/ContentContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { serviceCategories as CATEGORIES } from '@/data/content';
import {
  ClinicSchedule,
  ScheduleException,
  ServiceOverride,
  BusyBlock,
  effectiveRules,
  generateSlotsForDay,
  isDayBookable,
  wallToUtcMs,
} from '@/lib/schedule';

// Consultation is always available, not a category
const CONSULTATION_SERVICE: Service = {
  id: 'consultation-meeting',
  category: 'consultation',
  title: 'פגישת ייעוץ אישית',
  price: 'ללא עלות',
  duration: '30 דק׳',
};

type Service = {
  id: string;
  category: string;
  title: string;
  subtitle?: string;
  tagline?: string;
  price?: string | null;
  duration?: string;
};

const DAYS_HEBREW = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];
const MONTHS_HE = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];

const parseDurationMin = (d?: string) => {
  if (!d) return 0;
  const n = parseInt(d.replace(/\D/g, ''), 10);
  return isNaN(n) ? 0 : n;
};

// Default fallback schedule used until the live one loads.
const FALLBACK_SCHEDULE: ClinicSchedule = {
  weekly: Array.from({ length: 7 }, () => ({
    enabled: false, intervals: [], breaks: [],
  })),
  buffer_minutes: 10,
  slot_step_minutes: 15,
  min_lead_minutes: 120,
  horizon_days: 60,
  timezone: 'Asia/Jerusalem',
};

// Build month grid (weeks of 7)
const buildMonthGrid = (year: number, month: number) => {
  const first = new Date(year, month, 1);
  const startDay = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: ({ date: Date; day: number } | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), day: d });
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};




export const Appointment: React.FC = () => {
  const navigate = useNavigate();
  const appointmentHeading = useT('home.appointment.heading', 'במה תרצי להתמקד היום?');

  // State
  const [step, setStep] = useState(1);
  const [activeCategory, setActiveCategory] = useState<string>('hair');
  const [selectedTreatments, setSelectedTreatments] = useState<Service[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Contact form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Busy slots from Google Calendar (UTC ms intervals)
  const [busyIntervals, setBusyIntervals] = useState<BusyBlock[]>([]);
  const [busyLoading, setBusyLoading] = useState(false);

  // Admin-driven schedule
  const [schedule, setSchedule] = useState<ClinicSchedule>(FALLBACK_SCHEDULE);
  const [confirmationMessage, setConfirmationMessage] = useState<string>(
    'הבקשה שלך התקבלה. אנו נשלח אלייך הודעת וואטסאפ לאישור סופי.'
  );
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [overridesMap, setOverridesMap] = useState<Record<string, ServiceOverride>>({});
  const [scheduleLoaded, setScheduleLoaded] = useState(false);

  // Calendar month state
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [calMonth, setCalMonth] = useState<{ y: number; m: number }>({ y: today.getFullYear(), m: today.getMonth() });


  // Animation
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  const prevTreatmentsLength = useRef(selectedTreatments.length);
  const { services: mergedServices } = useContent();

  const servicesByCategory = useMemo<Service[]>(() => {
    return (mergedServices as unknown as Service[]).filter(s => s.category === activeCategory);
  }, [mergedServices, activeCategory]);

  // Resolve each selected treatment to a ServiceOverride (using DB row when
  // present, otherwise a permissive default built from the catalog).
  const selectedOverrides = useMemo<ServiceOverride[]>(() => {
    return selectedTreatments.map((t) => {
      const o = overridesMap[t.id];
      if (o) return o;
      return {
        shopify_product_id: t.id,
        bookable: true,
        duration_minutes: parseDurationMin(t.duration) || 60,
        allowed_weekdays: null,
        allowed_windows: null,
        buffer_minutes: null,
        slot_step_minutes: null,
        min_lead_minutes: null,
      };
    });
  }, [selectedTreatments, overridesMap]);

  const rules = useMemo(
    () => effectiveRules(schedule, selectedOverrides),
    [schedule, selectedOverrides],
  );
  const totalDuration = rules.total_duration;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setShouldLoadVideo(true);
        }
      },
      { threshold: 0.1, rootMargin: '300px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (sectionRef.current) {
          const top = sectionRef.current.getBoundingClientRect().top;
          const height = window.innerHeight;
          const progress = Math.max(0, Math.min(1, 1 - (top / height)));
          setScrollProgress(progress);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (selectedTreatments.length > 0 && prevTreatmentsLength.current === 0 && step === 1 && isMobile && ctaRef.current) {
      ctaRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    prevTreatmentsLength.current = selectedTreatments.length;
  }, [selectedTreatments.length, step, isMobile]);

  const handleNextStep = () => setStep(prev => prev + 1);
  const handlePrevStep = () => setStep(prev => prev - 1);

  const toggleTreatment = (t: Service) => {
    setSelectedTreatments(prev =>
      prev.find(s => s.id === t.id) ? prev.filter(s => s.id !== t.id) : [...prev, t]
    );
  };

  // Load admin schedule + exceptions + per-service overrides once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: sched }, { data: exc }, { data: ovr }] = await Promise.all([
        supabase.from('clinic_schedule').select('*').limit(1).maybeSingle(),
        supabase.from('schedule_exceptions').select('date,closed,intervals,note'),
        supabase.from('service_overrides').select('*'),
      ]);
      if (cancelled) return;
      if (sched) {
        setSchedule({
          weekly: sched.weekly as unknown as ClinicSchedule['weekly'],
          buffer_minutes: sched.buffer_minutes,
          slot_step_minutes: sched.slot_step_minutes,
          min_lead_minutes: sched.min_lead_minutes,
          horizon_days: sched.horizon_days,
          timezone: sched.timezone ?? 'Asia/Jerusalem',
        });
        if ((sched as any).confirmation_message) {
          setConfirmationMessage((sched as any).confirmation_message);
        }
      }
      setExceptions((exc as any) ?? []);
      const map: Record<string, ServiceOverride> = {};
      ((ovr as any[]) ?? []).forEach((o) => { map[o.shopify_product_id] = o; });
      setOverridesMap(map);
      setScheduleLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch busy intervals for the visible month whenever treatments are picked
  // or the month changes. Triggers as soon as we know the duration.
  useEffect(() => {
    if (selectedTreatments.length === 0 || totalDuration <= 0) {
      setBusyIntervals([]);
      return;
    }
    let cancelled = false;
    const fetchBusy = async () => {
      const rangeStart = new Date(Math.max(
        new Date(calMonth.y, calMonth.m, 1).getTime(),
        today.getTime(),
      ));
      const rangeEnd = new Date(calMonth.y, calMonth.m + 1, 7);
      setBusyLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('calendar-busy', {
          body: {
            timeMin: rangeStart.toISOString(),
            timeMax: rangeEnd.toISOString(),
          },
        });
        if (cancelled) return;
        if (error) throw error;
        const busy = ((data as any)?.busy ?? []) as { start: string; end: string }[];
        setBusyIntervals(
          busy.map(b => ({ start: new Date(b.start).getTime(), end: new Date(b.end).getTime() }))
        );
      } catch (err) {
        console.error('failed to load busy slots', err);
        if (!cancelled) setBusyIntervals([]);
      } finally {
        if (!cancelled) setBusyLoading(false);
      }
    };
    fetchBusy();
    return () => { cancelled = true; };
  }, [selectedTreatments.length, totalDuration, calMonth.y, calMonth.m, today]);

  const nowMs = useMemo(() => Date.now(), [calMonth.y, calMonth.m, busyIntervals]);

  // Generate available time slots for selected date based on full schedule.
  const availableSlots = useMemo<string[]>(() => {
    if (!selectedDate || totalDuration <= 0 || !scheduleLoaded) return [];
    return generateSlotsForDay(
      selectedDate, schedule, exceptions, selectedOverrides, busyIntervals, nowMs,
    );
  }, [selectedDate, totalDuration, scheduleLoaded, schedule, exceptions, selectedOverrides, busyIntervals, nowMs]);

  // Calendar grid
  const monthCells = useMemo(() => buildMonthGrid(calMonth.y, calMonth.m), [calMonth]);
  const goPrevMonth = () => setCalMonth(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 });
  const goNextMonth = () => setCalMonth(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 });
  const monthStart = new Date(calMonth.y, calMonth.m, 1);
  const canGoPrev = monthStart > new Date(today.getFullYear(), today.getMonth(), 1);

  const isDayAvailable = (d: Date) => {
    if (d < today) return false;
    if (!scheduleLoaded) return false;
    if (totalDuration <= 0) {
      // No duration yet — just gate by weekly enablement + horizon.
      const day = schedule.weekly[d.getDay()];
      if (!day?.enabled && !exceptions.find((e) => e.date && !e.closed && e.intervals?.length)) {
        // Closed by weekly + no opening exception.
        const exc = exceptions.find((e) => e.date === d.toISOString().slice(0, 10));
        if (!exc || exc.closed || (exc.intervals?.length ?? 0) === 0) return false;
      }
      const horizonMs = nowMs + schedule.horizon_days * 86_400_000;
      if (d.getTime() > horizonMs) return false;
      return true;
    }
    return isDayBookable(d, schedule, exceptions, selectedOverrides, busyIntervals, nowMs);
  };





  const summaryTitle = selectedTreatments.length === 0
    ? '...'
    : selectedTreatments.length === 1
      ? selectedTreatments[0].title
      : `${selectedTreatments.length} טיפולים`;

  const handleSubmitBooking = async () => {
    if (!selectedDate || !selectedTime || selectedTreatments.length === 0) return;
    if (!name.trim() || !phone.trim() || !email.trim()) {
      toast({ title: 'אנא מלאי את כל הפרטים', variant: 'destructive' });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast({ title: 'כתובת אימייל לא תקינה', variant: 'destructive' });
      return;
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    const startLocal = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}T${selectedTime}`;

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('book-appointment', {
        body: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          startLocal,
          durationMinutes: totalDuration,
          treatments: selectedTreatments.map((t) => t.title),
          treatmentIds: selectedTreatments.map((t) => t.id),
        },
      });
      if (error) throw error;
      const errCode = (data as any)?.error as string | undefined;
      if (errCode) {
        const messages: Record<string, { title: string; description?: string; back?: number }> = {
          slot_unavailable: { title: 'השעה הזו כבר תפוסה', description: 'נסי לבחור שעה אחרת', back: 3 },
          busy:             { title: 'השעה הזו כבר תפוסה', description: 'נסי לבחור שעה אחרת', back: 3 },
          past:             { title: 'לא ניתן להזמין בעבר', back: 3 },
          lead_time:        { title: 'דרוש זמן התראה ארוך יותר', description: 'בחרי שעה מאוחרת יותר', back: 3 },
          horizon:          { title: 'התאריך רחוק מדי', description: 'יש לבחור תאריך קרוב יותר', back: 2 },
          closed_day:       { title: 'הקליניקה סגורה ביום זה', back: 2 },
          outside_window:   { title: 'השעה מחוץ לשעות הפעילות', back: 3 },
          not_bookable:     { title: 'אחד הטיפולים לא זמין להזמנה', back: 1 },
        };
        const info = messages[errCode] ?? { title: 'לא הצלחנו לשמור את התור' };
        toast({ title: info.title, description: info.description, variant: 'destructive' });
        if (typeof info.back === 'number') setStep(info.back);
        return;
      }
      if (!(data as any)?.ok) {
        throw new Error('unknown_error');
      }
      setStep(5);

    } catch (err) {
      console.error('booking failed', err);
      toast({ title: 'לא הצלחנו לשמור את התור', description: 'נסי שוב או צרי איתנו קשר', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };




  return (
    <section
      id="appointment"
      ref={sectionRef}
      className="relative w-full py-20 md:py-40 bg-[#67392A] text-white overflow-hidden z-30"
    >
      {/* PARALLAX BACKGROUND VIDEO */}
      <div
        className="absolute inset-x-0 -top-32 -bottom-32 z-0 pointer-events-none will-change-transform bg-[#67392A]"
        style={{ transform: `translate3d(0, ${(scrollProgress - 0.5) * 160}px, 0) scale(1.15)` }}
      >
        {shouldLoadVideo && (
          <LazyVideo
            src="https://cdn.shopify.com/videos/c/o/v/db1c49bcaceb4a10830b72684185f355.mp4"
            autoPlay muted loop playsInline preload="metadata"
            onCanPlay={() => setVideoReady(true)}
            onLoadedData={() => setVideoReady(true)}
            className={`w-full h-full object-cover transition-opacity duration-700 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="absolute inset-0 z-0 pointer-events-none bg-gradient-to-b from-[#2a1a1e]/90 via-[#2a1a1e]/85 to-[#2a1a1e]/95" />
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none opacity-60">
        <LiquidBackground scrollProgress={scrollProgress} />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* --- HEADER --- */}
        <div className="flex flex-col items-center text-center mb-10 md:mb-16">
          <span className={`text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-3 md:mb-4 text-[#fbbf25] transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>הזמנת תור</span>
          <h2 className={`text-4xl sm:text-5xl md:text-8xl font-serif mb-4 md:mb-6 transition-all duration-1000 delay-100 leading-tight ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            שרייני את <span className="text-[#fbbf25]">הרגע שלך</span>
          </h2>
          <div className={`w-24 h-[1px] bg-white/10 transition-all duration-1000 delay-200 ${isVisible ? 'w-24' : 'w-0'}`}></div>
        </div>

        {/* --- MAIN WIDGET --- */}
        <div className={`max-w-6xl mx-auto flex flex-col md:flex-row shadow-2xl shadow-black/50 md:min-h-[600px] transition-all duration-1000 delay-300 bg-[#F9F7F2] border border-white/5 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>

          {/* LEFT SIDE: JOURNEY */}
          <div className="w-full md:w-2/3 p-5 sm:p-8 md:p-12 lg:p-16 relative overflow-hidden text-[#412018] order-1 md:order-1">

            {/* Step Indicators */}
            {step < 5 && (
              <div className="flex items-center gap-1.5 md:gap-2 mb-8 md:mb-12 text-[9px] md:text-[10px] uppercase tracking-widest text-[#412018]/30 overflow-x-auto hide-scrollbar">
                <span className={`whitespace-nowrap ${step >= 1 ? "text-[#67392b] font-bold" : ""}`}>01 טיפול</span>
                <span className="w-4 md:w-8 h-[1px] bg-current flex-shrink-0"></span>
                <span className={`whitespace-nowrap ${step >= 2 ? "text-[#67392b] font-bold" : ""}`}>02 תאריך</span>
                <span className="w-4 md:w-8 h-[1px] bg-current flex-shrink-0"></span>
                <span className={`whitespace-nowrap ${step >= 3 ? "text-[#67392b] font-bold" : ""}`}>03 שעה</span>
                <span className="w-4 md:w-8 h-[1px] bg-current flex-shrink-0"></span>
                <span className={`whitespace-nowrap ${step >= 4 ? "text-[#67392b] font-bold" : ""}`}>04 פרטים</span>
              </div>
            )}

            {/* STEP 1: SELECT TREATMENT(S) */}
            {step === 1 && (
              <div className="animate-fade-in-up">
                <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif mb-3 leading-tight text-[#412018]">{appointmentHeading}</h3>
                <p className="text-sm md:text-base text-[#412018]/60 mb-6 md:mb-8">ניתן לבחור מספר טיפולים לאותו תור</p>

                {/* Category tabs */}
                <div className="grid grid-cols-3 gap-1.5 md:gap-2 mb-4 md:mb-6">
                  {CATEGORIES.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setActiveCategory(c.id)}
                      className={`px-2 md:px-5 py-2.5 text-[10px] md:text-sm uppercase tracking-[0.1em] md:tracking-[0.15em] border transition-all duration-300 text-center leading-tight min-w-0 ${
                        activeCategory === c.id
                          ? 'bg-[#412018] text-[#fbbf25] border-[#412018]'
                          : 'bg-white text-[#412018]/70 border-[#412018]/10 hover:border-[#67392b] hover:text-[#67392b]'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                {/* Consultation option — always visible, not a category */}
                <button
                  onClick={() => toggleTreatment(CONSULTATION_SERVICE)}
                  className={`w-full mb-4 md:mb-6 p-5 md:p-6 border text-right transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
                    selectedTreatments.find(s => s.id === CONSULTATION_SERVICE.id)
                      ? 'bg-[#f4efe3] border-[#67392b] shadow-md'
                      : 'bg-white border-[#412018]/10 hover:border-[#67392b]'
                  }`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex flex-col">
                      <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#67392b] mb-1">אפשרות למתחילות</span>
                      <h4 className="text-base md:text-lg font-bold text-[#412018] leading-snug">{CONSULTATION_SERVICE.title}</h4>
                    </div>
                    {CONSULTATION_SERVICE.duration && <span className="text-[11px] md:text-xs font-bold text-[#412018]/40 whitespace-nowrap mt-1">{CONSULTATION_SERVICE.duration}</span>}
                  </div>
                  {CONSULTATION_SERVICE.price && (
                    <div className="mt-3">
                      <span className="font-sans font-light tabular-nums text-[#412018] text-lg leading-none">{CONSULTATION_SERVICE.price}</span>
                    </div>
                  )}
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 md:max-h-[500px] md:overflow-y-auto pr-1 -mr-1">
                  {servicesByCategory.map(t => {
                    const isSelected = !!selectedTreatments.find(s => s.id === t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggleTreatment(t)}
                        className={`group p-5 md:p-6 border text-right transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
                          isSelected ? 'bg-[#f4efe3] border-[#67392b] shadow-md' : 'bg-white border-[#412018]/10 hover:border-[#67392b]'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-3 mb-3 w-full">
                          <h4 className="text-base md:text-lg font-bold text-[#412018] group-hover:text-[#67392b] transition-colors leading-snug">{t.title}</h4>
                          {t.duration && <span className="text-[11px] md:text-xs font-bold text-[#412018]/40 whitespace-nowrap mt-1">{t.duration}</span>}
                        </div>
                        {t.price && (
                          <div className="mt-auto">
                            <span className="font-sans font-light tabular-nums text-[#412018] text-lg leading-none">{t.price}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

              </div>
            )}

            {/* STEP 2: SELECT DATE (month tile calendar) */}
            {step === 2 && (
              <div className="animate-fade-in-up">
                <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
                  <button onClick={handlePrevStep} className="p-2 border border-[#412018]/10 rounded-full hover:border-[#67392b] text-[#412018]"><ChevronRight size={16} /></button>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#412018]">בחרי תאריך</h3>
                </div>

                {/* Month switcher */}
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <button
                    onClick={goPrevMonth}
                    disabled={!canGoPrev}
                    className="p-2 border border-[#412018]/10 hover:border-[#67392b] text-[#412018] disabled:opacity-30 disabled:hover:border-[#412018]/10"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <div className="text-base md:text-xl font-bold text-[#412018]">
                    {MONTHS_HE[calMonth.m]} {calMonth.y}
                  </div>
                  <button onClick={goNextMonth} className="p-2 border border-[#412018]/10 hover:border-[#67392b] text-[#412018]">
                    <ChevronLeft size={18} />
                  </button>
                </div>

                {/* Weekday header */}
                <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 text-center text-[10px] md:text-xs text-[#412018]/40 uppercase tracking-widest">
                  {DAYS_HEBREW.map(d => <div key={d}>{d}</div>)}
                </div>

                {/* Day tiles */}
                <div className="grid grid-cols-7 gap-1 md:gap-2">
                  {monthCells.map((cell, i) => {
                    if (!cell) return <div key={i} />;
                    const avail = isDayAvailable(cell.date);
                    const isSelected = selectedDate && cell.date.toDateString() === selectedDate.toDateString();
                    return (
                      <button
                        key={i}
                        disabled={!avail}
                        onClick={() => { setSelectedDate(cell.date); handleNextStep(); }}
                        className={`aspect-square flex flex-col items-center justify-center border text-sm md:text-base transition-all duration-200 relative ${
                          isSelected
                            ? 'bg-[#412018] text-[#fbbf25] border-[#412018]'
                            : avail
                              ? 'bg-white border-[#412018]/10 text-[#412018] hover:border-[#67392b] hover:bg-[#67392b]/5'
                              : 'bg-[#412018]/5 border-transparent text-[#412018]/25 cursor-not-allowed line-through decoration-[#412018]/20'
                        }`}
                        title={avail ? '' : 'תפוס'}
                      >
                        <span className="text-lg md:text-xl font-medium">{cell.day}</span>
                        {!avail && cell.date >= today && (
                          <span className="text-[8px] md:text-[9px] uppercase tracking-widest mt-0.5 opacity-60">תפוס</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="text-[11px] md:text-xs text-[#412018]/50 mt-4 leading-relaxed">
                  ימים בהם הקלניקה תפוסה מוצגים באפור
                </p>
              </div>
            )}

            {/* STEP 3: SELECT TIME */}
            {step === 3 && (
              <div className="animate-fade-in-up">
                <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
                  <button onClick={handlePrevStep} className="p-2 border border-[#412018]/10 rounded-full hover:border-[#67392b] text-[#412018]"><ChevronRight size={16} /></button>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#412018]">בחרי שעה</h3>
                </div>

                {availableSlots.length === 0 ? (
                  <div className="bg-white border border-[#412018]/10 p-6 text-[#412018]/70 text-sm">
                    אין שעות פנויות באורך הנדרש ביום זה. אנא בחרי תאריך אחר.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 md:gap-3 max-w-2xl max-h-[420px] overflow-y-auto pr-1">
                    {availableSlots.map((time, i) => (
                      <button
                        key={time}
                        onClick={() => { setSelectedTime(time); handleNextStep(); }}
                        className={`py-3 md:py-4 border bg-white transition-all duration-200 text-base md:text-lg tracking-wide ${
                          selectedTime === time
                            ? 'border-[#67392b] bg-[#67392b] text-white'
                            : 'border-[#412018]/10 hover:border-[#67392b] hover:bg-[#67392b] hover:text-white text-[#412018]'
                        }`}
                        style={{ animation: `fadeIn 0.3s ease-out ${Math.min(i, 12) * 0.03}s backwards` }}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                )}

                <p className="text-[11px] md:text-xs text-[#412018]/50 mt-5">
                  השעות מותאמות אוטומטית למשך הטיפולים שבחרת.
                </p>
              </div>
            )}

            {/* STEP 4: FORM */}
            {step === 4 && (
              <div className="animate-fade-in-up">
                <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
                  <button onClick={handlePrevStep} className="p-2 border border-[#412018]/10 rounded-full hover:border-[#67392b] text-[#412018]"><ChevronRight size={16} /></button>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#412018]">פרטים אחרונים</h3>
                </div>

                <div className="space-y-6 md:space-y-8 max-w-lg">
                  <div className="space-y-4">
                    <div className="relative group">
                      <User className="absolute right-0 top-3 text-[#412018]/30 group-focus-within:text-[#67392b] transition-colors" size={20} />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="שם מלא"
                        autoComplete="name"
                        className="w-full pr-8 py-3 bg-transparent border-b border-[#412018]/20 focus:border-[#67392b] outline-none transition-colors text-right text-[#412018] placeholder-[#412018]/30"
                      />
                    </div>
                    <div className="relative group">
                      <span className="absolute right-0 top-3 text-[#412018]/30 group-focus-within:text-[#67392b] transition-colors">Tel</span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="מספר טלפון"
                        autoComplete="tel"
                        className="w-full pr-8 py-3 bg-transparent border-b border-[#412018]/20 focus:border-[#67392b] outline-none transition-colors text-right text-[#412018] placeholder-[#412018]/30"
                      />
                    </div>
                    <div className="relative group">
                      <span className="absolute right-0 top-3 text-[#412018]/30 group-focus-within:text-[#67392b] transition-colors text-xs">@</span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="אימייל (לקבלת הזמנה ליומן)"
                        autoComplete="email"
                        dir="ltr"
                        className="w-full pr-8 py-3 bg-transparent border-b border-[#412018]/20 focus:border-[#67392b] outline-none transition-colors text-right text-[#412018] placeholder-[#412018]/30"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-[#412018]/50 leading-relaxed">
                    בלחיצה על "אישור הזמנה", השעה תיחסם ביומן הקליניקה ותקבלי הזמנה ל-Google Calendar באימייל.
                  </p>

                  <button
                    onClick={handleSubmitBooking}
                    disabled={submitting}
                    className="w-full bg-[#412018] text-[#fbbf25] py-4 uppercase tracking-[0.2em] hover:bg-[#67392b] hover:text-white transition-colors duration-500 flex items-center justify-center gap-4 group font-bold text-sm md:text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>שולח...</span>
                      </>
                    ) : (
                      <>
                        <span>אשרי את הריטואל</span>
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </div>

              </div>
            )}

            {/* STEP 5: SUCCESS */}
            {step === 5 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-10 animate-slow-fade-up">
                <div className="w-20 h-20 md:w-24 md:h-24 border border-[#67392b] rounded-full flex items-center justify-center mb-6 md:mb-8 relative">
                  <div className="absolute inset-0 border border-[#67392b] rounded-full animate-ping opacity-20"></div>
                  <Check size={36} className="text-[#67392b]" />
                </div>
                <h3 className="text-3xl md:text-5xl font-bold mb-4 text-[#412018]">תודה</h3>
                <p className="text-base md:text-lg text-[#412018]/60 max-w-md mb-10 md:mb-12 px-4 whitespace-pre-line">
                  {confirmationMessage}
                </p>
                <button onClick={() => navigate('/')} className="text-xs uppercase tracking-widest border-b border-[#412018]/20 hover:border-[#412018] transition-colors pb-1 text-[#412018]">
                  חזרה לדף הבית
                </button>
              </div>
            )}
          </div>

          {/* RIGHT SIDE: SUMMARY */}
          <div className="w-full md:w-1/3 bg-[#EAE6D7] text-[#412018] p-5 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-between relative overflow-hidden border-t md:border-t-0 md:border-r border-[#412018]/5 order-2 md:order-2">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#67392b]/5 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>

            <div className="relative">
              <h4 className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#67392b] mb-6 md:mb-12">סיכום הזמנה</h4>

              <div className="grid grid-cols-1 gap-4 md:gap-0 md:space-y-8">
                <div className={`min-w-0 transition-all duration-700 ${selectedTreatments.length ? 'opacity-100 translate-x-0' : 'opacity-30 translate-x-4'}`}>
                  <label className="text-[9px] md:text-[10px] uppercase text-[#412018]/40 block mb-1.5 md:mb-2">הטיפול</label>
                  <div className="text-sm md:text-2xl font-bold text-[#412018] leading-snug break-words">{summaryTitle}</div>
                  {selectedTreatments.length > 1 && (
                    <ul className="block mt-3 space-y-1 text-xs text-[#412018]/60 list-disc pr-4">
                      {selectedTreatments.map(s => <li key={s.id}>{s.title}</li>)}
                    </ul>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-1 gap-4 md:gap-0 md:space-y-8">
                  <div className={`min-w-0 transition-all duration-700 delay-100 ${selectedDate ? 'opacity-100 translate-x-0' : 'opacity-30 translate-x-4'}`}>
                    <label className="text-[9px] md:text-[10px] uppercase text-[#412018]/40 block mb-1.5 md:mb-2">תאריך</label>
                    <div className="text-sm md:text-2xl font-bold text-[#412018] break-words">
                      {selectedDate ? `${DAYS_HEBREW[selectedDate.getDay()]}, ${selectedDate.getDate()} ${MONTHS_HE[selectedDate.getMonth()]}` : '...'}
                    </div>
                  </div>

                  <div className={`min-w-0 transition-all duration-700 delay-200 ${selectedTime ? 'opacity-100 translate-x-0' : 'opacity-30 translate-x-4'}`}>
                    <label className="text-[9px] md:text-[10px] uppercase text-[#412018]/40 block mb-1.5 md:mb-2">שעה</label>
                    <div className="text-sm md:text-2xl font-bold text-[#412018]">
                      {selectedTime ? selectedTime : '...'}
                    </div>
                  </div>
                </div>
              </div>

              {step === 1 && selectedTreatments.length > 0 && (
                <button
                  ref={ctaRef}
                  onClick={handleNextStep}
                  className="mt-6 md:mt-10 w-full bg-[#412018] text-[#fbbf25] px-6 py-3.5 uppercase tracking-[0.2em] hover:bg-[#67392b] hover:text-white transition-colors text-xs md:text-sm font-bold inline-flex items-center justify-center gap-3"
                >
                  <span>{selectedTreatments.length} נבחרו · המשך</span>
                  <ArrowLeft size={14} />
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
